// Copyright 2025, University of Maryland, All Rights Reserved
/**
 * Fail Type selector for I-9 documents
 *
 * A reviewer-selected failure category for documents that didn't match. Setting it
 * doesn't change the document's status or move it between tabs. Shown only on the
 * No Match and Partial Match tabs.
 */
import type { FC } from 'react'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'

// Allowed failure categories, kept in sync with the backend FailType literal.
export const FAIL_TYPES = [
  'OCR Error',
  'Illegible Handwriting',
  'Not an I-9 Form',
  'Incomplete or Incorrect I-9',
  'Poor Scan Quality',
] as const

export type FailType = (typeof FAIL_TYPES)[number]

// Radix Select forbids an empty-string item value, so a sentinel carries "clear".
const CLEAR_VALUE = '__clear__'

interface FailTypeSelectProps {
  value: FailType | null
  disabled?: boolean
  onChange: (value: FailType | null) => void
}

export const FailTypeSelect: FC<FailTypeSelectProps> = ({ value, disabled, onChange }) => (
  <Select
    value={value ?? ''}
    onValueChange={(v) => onChange(v === CLEAR_VALUE ? null : (v as FailType))}
    disabled={disabled}
  >
    <SelectTrigger className="w-52" aria-label="Fail type">
      <SelectValue placeholder="Select…" />
    </SelectTrigger>
    <SelectContent>
      {value && (
        <SelectItem value={CLEAR_VALUE} className="text-muted-foreground">
          — Clear —
        </SelectItem>
      )}
      {FAIL_TYPES.map((t) => (
        <SelectItem key={t} value={t}>
          {t}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
)
